// @flow
import * as authPages from 'app/www/bundleLoaders';
import * as dr from 'app/common/lib/deeplinkRoutes';
import * as pages from 'app/www-unauth/bundleLoaders';
import App from 'app/www-unauth/components/App/App';
import store from 'app/common/redux/store/singleton';
import type AExperimentsClient from 'app/common/lib/ExperimentsClient/AExperimentsClient';
import { addReducer } from 'app/www-unauth/redux/reducers';
import { appendQuery } from 'app/packages/url';
import type { State, Location } from 'app/common/lib/ReactRouter/types';
import { Redirect, Route } from 'app/common/lib/ReactRouter/route';

const pinsReducerLoader = () => import('app/common/redux/reducers/pins');

type Context = Object;

type RouteArgs = {
  legacy_server_context: Context,
  experiments: AExperimentsClient,
  seoUnauthExperiments: AExperimentsClient,
};

export default function routesFactory(routeArgs: RouteArgs) {
  const { legacy_server_context, experiments, seoUnauthExperiments } = routeArgs;
  const r = (path: *, pageType: string, { getComponent, chunkName }, args?: *) =>
    new Route({
      path,
      pageType,
      legacy_server_context,
      getComponent,
      chunkName,
      ...args,
    });

  const indexRoute = (pageType: string, { getComponent, chunkName }, args?: *) =>
    new Route({
      pageType,
      legacy_server_context,
      getComponent,
      chunkName,
      ...args,
    });

  const redirect = (from: string, to: string | ((state: State) => $Shape<Location>)) =>
    new Redirect(
      from,
      (nextState, replace) => {
        if (typeof to === 'string') {
          replace({ pathname: to, query: nextState.location.query });
        } else {
          replace(to(nextState));
        }
      },
      legacy_server_context,
    );

  const routes = new Route({
    path: '/',
    component: App,
    bundle: 'entryChunk-www-unauth',
    legacy_server_context,
    indexRoute: indexRoute('home', pages.UnauthHome, {
      deeplinks: dr.buildHomeFeedDeeplinks,
    }),

    childRoutes: [
      r('/_/_/login', 'login', pages.Login),
      // DMCA takedown form
      r(
        '/about/copyright/dmca-pin/web-result/:id',
        'TakedownFormWebResultId',
        authPages.TakedownForm,
      ),
      r('/about/copyright/dmca-pin/pin/:id', 'TakedownFormPinId', authPages.TakedownForm),
      r('/about/copyright/dmca-pin/pin/', 'TakedownForm', authPages.TakedownForm),
      r('/about/copyright/dmca-pin/', 'TakedownFormPin', authPages.TakedownForm),
      r('/about/trademark/form/:claim_type/', 'TrademarkForm', authPages.TrademarkForm),
      r('/about/trademark/form/', 'TrademarkFormSelector', authPages.TrademarkFormSelector),
      redirect(
        '/business/ad_create',
        appendQuery('/business/create', {
          next: '/business/ad_create',
        }),
      ),
      r('/business/create', 'businessCreate', pages.BusinessCreatePage),
      r('/email/remove', 'email', pages.EmailDisassociatePage),
      r('/email/subscription', 'email', pages.EmailUnsubscribePage),
      r('/email_verification_error', 'email', pages.EmailVerificationErrorPage),
      r('/csrf_error', 'csrfError', authPages.CsrfError),
      r('/login', 'login', pages.Login),
      r(
        '/parental-consent/confirmation',
        'parentalConsentConfirmation',
        pages.ParentalConsentConfirmation,
      ),
      r('/password/reset', 'passwordReset', pages.PasswordResetPage),
      r('/pw/:username', 'passwordUserName', pages.UnauthPasswordUserNamePage),
      r('/invited', 'invited', pages.InvitedPage),
      r(
        '/pin/:id',
        'pin',
        pages.UnauthPinPageContainer(seoUnauthExperiments, legacy_server_context.is_bot === 'true'),
        {
          deeplinks: ({ id }: *) => dr.buildPinDeeplinks(id),
          authBundleLoader: authPages.Closeup.loader,
          authResources: [
            {
              name: 'PinResource',
              options: (params) => ({
                id: params.id,
                field_set_key: 'detailed',
                pure_react: true,
                is_landing_page: null,
                is_autologin: null,
              }),
            },
          ],
        },
      ),
      r('/pin/:pin_id/sent', 'pinFeedBack', pages.PinFeedbackPage, {
        deeplinks: /enabled/.test(
          seoUnauthExperiments.v2GetGroup('web_unauth_pin_feedback_deeplink') || '',
        )
          ? ({ pin_id }: *) => dr.buildPinDeeplinks(pin_id)
          : undefined,
      }),
      r('/pin/:pin_id/feedback/', 'pinFeedBack', pages.PinFeedbackPage),
      redirect('/pin/:id/activity', ({ params }) => ({ pathname: `/pin/${params.id}/` })),
      redirect('/pin/:id/activity/saved', ({ params }) => ({ pathname: `/pin/${params.id}/` })),
      redirect('/pin/:id/activity/tried', ({ params }) => ({ pathname: `/pin/${params.id}/` })),
      redirect('/pin/:id/comments', ({ params }) => ({ pathname: `/pin/${params.id}/` })),
      redirect('/pin/:id/edit', ({ params }) => ({ pathname: `/pin/${params.id}/` })),
      redirect('/pin/:id/likes', ({ params }) => ({ pathname: `/pin/${params.id}/` })),
      redirect('/pin/:id/promote', ({ params }) => ({ pathname: `/pin/${params.id}/` })),
      redirect('/pin/:id/repin', ({ params }) => ({ pathname: `/pin/${params.id}/` })),
      redirect('/pin/:id/repins', ({ params }) => ({ pathname: `/pin/${params.id}/` })),
      r('/product-feed/', 'productFeed', pages.UnauthHome, {
        onEnter({ params }, replace) {
          replace({
            pathname: '/login/',
            query: {
              next: '/product-feed/',
            },
          });
        },
      }),
      r('/product-catalogs/', 'productCatalogs', pages.Catalogs),
      redirect('/search', '/search/pins'),
      r('/search/:scope', 'search', pages.Search, {
        onEnter: ({ location: { query = {} } }, replace) => {
          if ((query.q || '').trim().length === 0) {
            replace('/');
          }
        },
        deeplinks: ({ scope }: *, { q }: *) => dr.buildScopedSearchDeeplinks(scope, q),
      }),
      r('/source/:domain', 'source', pages.DomainFeed, {
        onEnter(nextState, replace, callback: () => void) {
          pinsReducerLoader().then(({ default: pinsReducer }) => {
            addReducer(store.instance, 'pins', pinsReducer);
            callback();
          });
        },
      }),
      r('/topics/:interest', 'topic', pages.UnauthTopicPageContainer, {
        deeplinks: ({ interest }: *) => dr.buildTopicDeeplinks(interest),
      }),
      r('/explore/:interest', 'klp', pages.UnauthInterestFeedPageContainer, {
        deeplinks: ({ interest }: *) => dr.buildExploreDeeplinks(interest),
        authBundleLoader: authPages.InterestFeed.loader,
        authResources: [
          {
            name: 'InterestResource',
            options: (props) => ({
              interest: props.interest,
              main_module_name: 'InterestFeedPage',
              pure_react: true,
            }),
          },
        ],
      }),
      r('/tablet', 'tabletPromoPage', pages.MobileLandingPage),
      r('/android', 'androidPromoPage', pages.MobileLandingPage),
      r('/ios', 'iosPromoPage', pages.MobileLandingPage),
      r('/ipad', 'ipadPromoPage', pages.MobileLandingPage),
      r('/iphone', 'iphonePromoPage', pages.MobileLandingPage),
      r('/mobile', 'mobilePromoPage', pages.MobileLandingPage),
      r('/:username/followers', 'profileFollowers', pages.FollowersPage),
      ...['following', 'likes', 'liked-by', 'pins', 'boards'].map((subPath) =>
        redirect(`/:username/${subPath}`, ({ params }) => ({
          pathname: `/${params.username}/`,
        })),
      ),
      r('/:username/', 'profile', pages.ProfilePage, {
        indexRoute: indexRoute('profileOverfiew', pages.ProfileOverview, {
          deeplinks: ({ username }: *) => dr.buildUserDeeplinks(username),
        }),
        childRoutes: [],
      }),
      redirect('/nav_bar', '/'),
      r('/_/_/reactperf1', 'reactPerf1', pages.BenchmarkPage),
      r(
        '/_/_/test_react_hooks',
        'testReactHooks',
        legacy_server_context.is_internal_ip ? pages.ReactHooksDemoPage : pages.Error404,
      ),
      r(
        '/_/_/test_loadable',
        'testLoadable',
        legacy_server_context.is_internal_ip ? pages.LoadableDemoPage : pages.Error404,
      ),
      r('/:username/:slug/', 'board', pages.UnauthBoardPageContainer(experiments), {
        deeplinks: ({ username, slug }: *) => dr.buildBoardDeeplinks(username, slug),
      }),
      r('/:username/:slug/:section_slug/', 'board', pages.UnauthBoardPageContainer(experiments), {
        onEnter({ params }, replace) {
          replace(`/${params.username}/${params.slug}/`);
        },
      }),
      r('*', '404', pages.Error404),
    ],
  });

  return routes;
}
